import Dialog from "@mui/material/Dialog";
import isEmpty from "lodash/isEmpty";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSnackbar } from "../internal/SnackbarProvider";
import React from "react";
import { useCallbacks } from "../../state/InternalContext";
import { useCreateInternalStateBranch } from "../../state/InternalContext";
import { useSiteContext } from "../../state/SiteContext";
import { services } from "../../globals";
import { Callback } from "../../state/state-types";

interface FormManagerProps {
    name: string,
    requiredFields: string,
    children: React.ReactNode,
}

/***
 * A component that renders a form and handles callbacks for buttons in the form.
 */
export function FormManager({ name, requiredFields, children }: FormManagerProps): JSX.Element {
    const createInternalStateBranch = useCreateInternalStateBranch();
    const { siteState } = useSiteContext();

    const callbacks = useCallbacks();
    const { showSnackbar } = useSnackbar();

    const [isOpen, setOpen] = useState<boolean>(false);
    const [fields, setFields] = useState<Record<string, string | boolean>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);

    /**
     * Split the required fields into an array of strings
     */
    const requiredFieldsArray: Array<string> = requiredFields ? requiredFields.split(",").map((field) => field.trim()) : [];

    const hasFieldValue = useCallback((value: string | boolean | undefined): boolean => {
        if (typeof value === "string") {
            return value.trim().length > 0;
        }
        return value === true;
    }, []);

    /***
    * The form is valid when all required fields have a non-empty text value.
    * Keep the existing "at least one field has value" guard for submit behavior.
    */
    const isValidForm = !isEmpty(fields) && requiredFieldsArray.every((field) => hasFieldValue(fields[field]));
    const formStateRef = useRef({
        fields,
        isValidForm,
        siteName: siteState.site?.name,
        submitError,
    });

    // Keep ref in sync during render so callbacks (e.g. `CallbackButton`) can read
    // the latest validity value in the same interaction cycle (no stale `useEffect` reads).
    formStateRef.current = {
        fields,
        isValidForm,
        siteName: siteState.site?.name,
        submitError,
    };

    const openDialog = useCallback(() => {
        setSubmitError(null);
        setOpen(true);
    }, [setOpen]);

    const closeDialog = useCallback(() => {
        setSubmitError(null);
        setOpen(false);
    }, [setOpen]);

    const changeFieldValue = useCallback((args: { [key: string]: any }) => {
        const field = args?.field;
        const hasValue = Object.prototype.hasOwnProperty.call(args || {}, "value");

        if (!field || !hasValue) {
            return;
        }

        setFields((previousFields) => {
            const nextValue = args.value as string | boolean;
            // Some blur flows emit an undefined value for the previous field; ignore those updates
            // so we do not accidentally clear valid form state during focus transitions.
            if (typeof nextValue === "undefined") {
                return previousFields;
            }
            if (previousFields[field] === nextValue) {
                return previousFields;
            }
            return {
                ...previousFields,
                [field]: nextValue,
            };
        });
    }, []);

    const sendFormToServer = useCallback(async () => {
        const { fields: currentFields, isValidForm: currentIsValidForm, siteName, submitError: currentSubmitError } = formStateRef.current;
        if (siteName) {
            // Only send the form if at least one field is filled in and all required fields are filled in
            if (currentIsValidForm && !currentSubmitError) {
                try {
                    const result = await services.form.submitForm(siteName, name, currentFields);
                    if (result.success) {
                        showSnackbar("Your message has been registered.", "success");
                        closeDialog();
                    } else {
                        setSubmitError(result.message || "Could not submit form.");
                    }
                } catch (_error) {
                    setSubmitError("Could not submit form.");
                }
            }
        }
    }, [name, closeDialog, showSnackbar]);

    const outsideFormTemplate: Array<React.ReactNode> = [];
    const formContent: Array<React.ReactNode> = [];

    // Extract the outside form template and form content from the children
    React.Children.forEach(children, child => {
        if (React.isValidElement(child) && (child.type.toString().toLowerCase() === "outsideformtemplate")) {
            outsideFormTemplate.push(child);
        }
        else {
            formContent.push(child);
        }
    })

    // Create a new state branch used in child components in the form
    // and add the callbacks to the new state branch

    const newCallbacks: Record<string, Callback> = useMemo(() => {
        return {
            ...(callbacks || {}),
            "open-dialog": { func: openDialog, enabledFunc: () => true },
            "close-dialog": { func: closeDialog, enabledFunc: () => true },
            "set-form-field": { func: changeFieldValue, enabledFunc: () => true },
            // Expose validity through a stable callback so form buttons can react
            // without recreating the branch and remounting inputs.
            "__is-valid-form": { func: () => undefined, enabledFunc: () => formStateRef.current.isValidForm },
            "submit-form": { func: sendFormToServer, enabledFunc: () => !formStateRef.current.submitError },
        }
    }, [callbacks, openDialog, closeDialog, changeFieldValue, sendFormToServer]);

    const BranchComponent = useMemo(() => createInternalStateBranch({
        callbacks: newCallbacks,
        isValidForm: false,
    }), [newCallbacks, createInternalStateBranch]);
    return <BranchComponent>
        {outsideFormTemplate}
        <Dialog open={isOpen} maxWidth={"md"} fullWidth={true}>
            {formContent}
            {submitError && <div style={{ color: "red", padding: "10px" }}>{submitError}</div>}
        </Dialog>
    </BranchComponent>;
};
